import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ChartLineUp, Target, Crosshair, Trophy, Crown, Lock, TrendUp, TrendDown, Flame,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import TeamBadge from "../components/TeamBadge";

const YELLOW = "#E3FF00";
const DIM = "#5A5F6B";

export default function Stats() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (!user.is_premier && !user.is_admin) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await apiClient.get("/me/stats");
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load stats");
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (!user) return null;

  if (!user.is_premier && !user.is_admin) {
    return <Paywall />;
  }

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-white/50 uppercase tracking-widest text-sm">Loading…</div>;
  }
  if (error) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-red-400">{error}</div>;
  }
  if (!data) return null;

  const { kpis, per_matchweek: perMw, best_matchweek: bestMw, streak_matchweeks: streak, outcome_breakdown: outcomes, best_teams: bestTeams, worst_teams: worstTeams } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">
            <Crown size={12} weight="fill" /> Premier only
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl uppercase font-black mt-3 flex items-center gap-3">
            <ChartLineUp weight="duotone" /> Your Stats
          </h1>
          <p className="text-white/60 mt-2 text-sm max-w-xl">
            Predictions are stronger with feedback. Hunt the patterns. Sharpen the picks.
          </p>
        </div>
      </header>

      {kpis.predictions === 0 && (
        <EmptyState />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
        <Kpi label="Total points" value={kpis.total_points} accent />
        <Kpi label="Picks made" value={kpis.predictions} />
        <Kpi label="Exact scores" value={kpis.exact_scores} suffix={<Target size={14} className="text-primary-yellow" />} />
        <Kpi label="Accuracy" value={`${kpis.accuracy_pct}%`} sub="≥1 pt per pick" />
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        <Highlight
          icon={<Trophy size={20} weight="fill" className="text-primary-yellow" />}
          label="Best matchweek"
          value={bestMw ? `MW ${bestMw.matchweek}` : "—"}
          sub={bestMw ? `${bestMw.points} pts · ${bestMw.exact} exact` : "Make some picks"}
        />
        <Highlight
          icon={<Flame size={20} weight="fill" className="text-primary-yellow" />}
          label="Active streak"
          value={`${streak} ${streak === 1 ? "week" : "weeks"}`}
          sub="Consecutive matchweeks predicted"
        />
        <Highlight
          icon={<Crosshair size={20} weight="fill" className="text-primary-yellow" />}
          label="Avg / pick"
          value={`${kpis.avg_points}`}
          sub="Points per prediction"
        />
      </div>

      {/* Form chart */}
      <section>
        <SectionTitle title="Form by matchweek" />
        <div className="surface border border-white/10 p-6">
          {perMw.length === 0 ? (
            <p className="text-white/40 text-sm uppercase tracking-widest">No matchweeks played yet.</p>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={perMw} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="matchweek" stroke={DIM} tick={{ fill: DIM, fontSize: 11 }} tickFormatter={(v) => `MW${v}`} />
                  <YAxis stroke={DIM} tick={{ fill: DIM, fontSize: 11 }} allowDecimals={false} />
                  <RTooltip
                    cursor={{ fill: "rgba(227,255,0,0.06)" }}
                    contentStyle={{ background: "#0F1116", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }}
                    formatter={(v, n, p) => [`${v} ${n === "points" ? "pts" : ""}`, n]}
                    labelFormatter={(v) => `Matchweek ${v}`}
                  />
                  <Bar dataKey="points" radius={[2, 2, 0, 0]}>
                    {perMw.map((d, i) => (
                      <Cell key={i} fill={bestMw && d.matchweek === bestMw.matchweek ? YELLOW : "rgba(227,255,0,0.55)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* Outcome breakdown */}
      <section>
        <SectionTitle title="Outcome breakdown" subtitle="How well you call the result type" />
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          <OutcomeCard label="Home wins" data={outcomes.home_win} />
          <OutcomeCard label="Draws" data={outcomes.draw} />
          <OutcomeCard label="Away wins" data={outcomes.away_win} />
        </div>
      </section>

      {/* By team */}
      <section className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        <TeamList title="Your money clubs" subtitle="Where you score most" Icon={TrendUp} accent="text-primary-yellow" rows={bestTeams} />
        <TeamList title="Pain clubs" subtitle="Two or more picks, fewest points" Icon={TrendDown} accent="text-red-400" rows={worstTeams} />
      </section>
    </div>
  );
}

const Kpi = ({ label, value, sub, suffix, accent }) => (
  <div className="surface p-6">
    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">{label}</div>
    <div className={`mt-2 font-mono-num text-4xl font-black flex items-center gap-2 ${accent ? "text-primary-yellow" : ""}`}>
      {value}
      {suffix}
    </div>
    {sub && <div className="text-[10px] uppercase tracking-widest text-white/30 mt-1">{sub}</div>}
  </div>
);

const Highlight = ({ icon, label, value, sub }) => (
  <div className="surface p-6 flex items-start gap-4">
    <div className="shrink-0 w-10 h-10 grid place-items-center bg-primary-yellow/10 border border-primary-yellow/30">
      {icon}
    </div>
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">{label}</div>
      <div className="mt-1 font-heading text-2xl uppercase font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{sub}</div>
    </div>
  </div>
);

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-4">
    <h2 className="font-heading text-2xl uppercase font-black">{title}</h2>
    {subtitle && <p className="text-white/50 text-xs uppercase tracking-widest mt-1">{subtitle}</p>}
  </div>
);

const OutcomeCard = ({ label, data }) => {
  const pct = data.predicted > 0 ? Math.round((100 * data.correct) / data.predicted) : 0;
  return (
    <div className="surface p-6">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-mono-num text-4xl font-black text-primary-yellow">{pct}%</div>
        <div className="text-xs text-white/40">accuracy</div>
      </div>
      <div className="mt-4 h-1.5 bg-white/5 overflow-hidden">
        <div className="h-full bg-primary-yellow" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40">
        {data.correct} correct of {data.predicted} picked
      </div>
    </div>
  );
};

const TeamList = ({ title, subtitle, Icon, accent, rows }) => (
  <div className="surface p-6">
    <div className="flex items-center gap-2">
      <Icon size={18} weight="fill" className={accent} />
      <h3 className="font-heading text-xl uppercase font-black">{title}</h3>
    </div>
    <p className="text-white/50 text-xs uppercase tracking-widest mt-1">{subtitle}</p>
    {rows.length === 0 ? (
      <p className="mt-6 text-white/40 text-sm uppercase tracking-widest">Not enough picks yet.</p>
    ) : (
      <ul className="mt-6 space-y-3">
        {rows.map((t, i) => (
          <li key={t.team} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono-num text-white/40 text-sm w-5">#{i + 1}</span>
              <TeamBadge team={t.team} />
              <span className="font-semibold uppercase tracking-wide text-sm truncate">{t.team}</span>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <span className="text-[10px] uppercase tracking-widest text-white/40">{t.predictions} picks</span>
              <span className="font-mono-num text-lg font-black text-primary-yellow">{t.points}</span>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const EmptyState = () => (
  <div className="surface border border-white/10 p-10 text-center">
    <Crosshair size={32} className="mx-auto text-primary-yellow" />
    <h3 className="font-heading text-2xl uppercase font-black mt-4">No data yet</h3>
    <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">
      Make a few predictions and check back after matchweek closes. Your charts populate as fixtures finish.
    </p>
    <Link
      to="/fixtures"
      className="mt-6 inline-flex items-center gap-2 bg-primary-yellow text-black px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold hover:-translate-y-0.5 transition-transform"
    >
      Make Picks
    </Link>
  </div>
);

const Paywall = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="surface-elevated border border-primary-yellow/30 glow-yellow p-10 text-center relative">
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-yellow text-black text-[10px] uppercase tracking-[0.25em] font-bold">
        Premier perk
      </span>
      <div className="mx-auto w-14 h-14 grid place-items-center bg-primary-yellow text-black rounded-full">
        <ChartLineUp size={28} weight="fill" />
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl uppercase font-black mt-6">Detailed stats are Premier</h1>
      <p className="text-white/60 mt-3 max-w-md mx-auto text-sm">
        Form charts, outcome breakdowns, your best and worst clubs, exact-score hit rate, and active streaks.
        £9.99 unlocks the full season.
      </p>
      <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-left text-sm max-w-md mx-auto">
        {[
          "Points-per-matchweek chart",
          "Home / Draw / Away accuracy",
          "Your money clubs",
          "Active matchweek streak",
        ].map((f) => (
          <li key={f} className="flex items-start gap-2 text-white/80">
            <Lock size={14} weight="bold" className="text-primary-yellow mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <Link
        data-testid="stats-upgrade-cta"
        to="/pricing"
        className="mt-8 inline-flex items-center gap-2 bg-primary-yellow text-black px-6 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:-translate-y-0.5 transition-transform"
      >
        <Crown size={14} weight="fill" /> Go Premier · £9.99
      </Link>
    </div>
  </div>
);
